// Canonical Zod schema for user preferences.
//
// Why Zod at the persistence boundary:
//   - `.catch()` makes parse infallible — any shape drift falls back to a
//     well-defined default rather than propagating a TypeError deep into
//     React render
//   - Adding / renaming / removing a preference key becomes a single-file
//     change: update the schema, defaults flow from `.catch()`, and stored
//     data from older app versions silently converges on the new shape
//   - The `Preferences` TypeScript type is *derived* from the schema
//     (`z.infer<>`), so the type cannot drift from the validator
//
// Scope: this is the one schema. Other persisted state (sidebar config is
// nested here; Dexie row schemas are separate and live in db.ts) follows
// the same pattern when it gets one. See ADR 0003 for the broader vision.

import { z } from 'zod'

// ─── Removed keys ────────────────────────────────────────────────────────────
//
// Sidebar keys that were valid in a previous version of the app and may
// still be present in preferences stored by older installations. Strip on
// read so stale configs don't resurrect removed UI.
//
// Remove entries from this set only when you're certain no user has a
// preferences row referencing them any more — once removed here, a row
// that still contains the key will render as a dead sidebar entry with
// no matching view.

export const REMOVED_SIDEBAR_KEYS = new Set<string>([
  // 'UNREAD' was folded into the Inbox view as a toggle; the old standalone
  // sidebar entry is gone. See Shell's inboxView state and the "Unread"
  // item in the INBOX filter menu.
  'UNREAD',
])

// ─── SidebarItem ─────────────────────────────────────────────────────────────

export const sidebarItemSchema = z.object({
  /** Uppercase stable key matching a filter slug (e.g. "INBOX", "STARRED"). */
  key: z.string().min(1).max(64),
  /** Display label shown in the sidebar. */
  label: z.string().max(128),
  /** Emoji icon; 1–2 grapheme clusters in practice but we permit up to 16. */
  emoji: z.string().max(16),
  /** Whether this item is currently shown. */
  visible: z.boolean(),
})

export type SidebarItem = z.infer<typeof sidebarItemSchema>

// ─── Default sidebar ─────────────────────────────────────────────────────────
//
// Appended to parsed configs so a newly-introduced default item shows up
// for existing users without a migration. Order matters — this is the
// canonical order for first-time users and for new-item append order.

export const DEFAULT_SIDEBAR: SidebarItem[] = [
  { key: 'INBOX', label: 'Inbox', emoji: '📥', visible: true },
  { key: 'STARRED', label: 'Starred', emoji: '⭐', visible: true },
  { key: 'DRAFTS', label: 'Drafts', emoji: '📝', visible: true },
  { key: 'REMINDERS', label: 'Reminders', emoji: '⏰', visible: true },
  { key: 'ARCHIVE', label: 'Archive', emoji: '📦', visible: true },
  { key: 'INMAIL', label: 'InMail', emoji: '💎', visible: true },
  { key: 'SPONSORED', label: 'Sponsored', emoji: '📢', visible: true },
  { key: 'SPAM', label: 'Spam', emoji: '🚫', visible: true },
  { key: 'SALESNAV', label: 'Sales Nav', emoji: '🧭', visible: true },
  // Recruiter (kondo-parity 1.10). Gated in the Sidebar component by
  // a `hasRecruiter` probe (analogous to `hasSalesNav`) so users
  // without a Recruiter account don't see the entry — but the
  // sidebar-item registry carries it unconditionally, which makes
  // the Organize Sidebar toggle + the sidebar reorder stable across
  // account changes.
  { key: 'RECRUITER', label: 'Recruiter', emoji: '💼', visible: true },
]

// ─── Preferences ─────────────────────────────────────────────────────────────
//
// Per-field `.catch()` makes each key self-healing. A corrupt stored value
// for one key doesn't taint the rest — they each individually fall back to
// their default.
//
// For `sidebarConfig` we use a two-phase transform:
//   1. Accept `z.array(z.unknown())` — any array shape at all. `.catch([])`
//      means an entirely non-array value falls back to empty.
//   2. Per-item `safeParse` with `sidebarItemSchema` — invalid items are
//      dropped, valid items kept. A single bad entry doesn't collapse the
//      whole list to default.
//   3. Strip removed keys (see `REMOVED_SIDEBAR_KEYS`).
//
// The "append any new DEFAULT_SIDEBAR entries the stored config didn't
// have" behaviour is applied in `parsePreferences` below, not in the
// schema, so the schema stays a pure shape-describer.

export const preferencesSchema = z.object({
  withdrawThreshold: z
    .number()
    .int()
    .min(1)
    .max(365)
    .catch(21),
  defaultView: z
    .string()
    .min(1)
    .max(64)
    .catch('INBOX'),
  theme: z
    .enum(['system', 'light', 'dark'])
    .catch('system'),
  sidebarConfig: z
    .array(z.unknown())
    .catch([])
    .transform((arr) =>
      arr.flatMap((raw) => {
        const parsed = sidebarItemSchema.safeParse(raw)
        return parsed.success ? [parsed.data] : []
      }),
    )
    .transform((items) => items.filter((i) => !REMOVED_SIDEBAR_KEYS.has(i.key))),
})

export type Preferences = z.infer<typeof preferencesSchema>

// ─── Defaults ────────────────────────────────────────────────────────────────
//
// Exported for callers that want to seed a fresh preference object. Also
// used as the terminal fallback inside `parsePreferences` if parse *itself*
// throws (shouldn't happen given `.catch()` but belt-and-braces).

export const DEFAULTS: Preferences = {
  withdrawThreshold: 21,
  defaultView: 'INBOX',
  theme: 'system',
  sidebarConfig: DEFAULT_SIDEBAR,
}

// ─── Parse ───────────────────────────────────────────────────────────────────

/**
 * Parse arbitrary input into a valid `Preferences`. Infallible — any parse
 * disaster funnels through `.catch()` defaults. Null / undefined input
 * returns a fresh copy of `DEFAULTS`.
 *
 * After parse, appends any `DEFAULT_SIDEBAR` entries the stored config
 * didn't have. That's how a newly-added default sidebar item shows up for
 * existing users without an explicit migration.
 */
export function parsePreferences(raw: unknown): Preferences {
  if (raw == null) return freshDefaults()

  let parsed: Preferences
  try {
    // `safeParse` + `.catch()` together — if the top-level shape is
    // completely unrecognisable (e.g. a string where an object was
    // expected), `safeParse` fails and we take the `freshDefaults` branch.
    const result = preferencesSchema.safeParse(raw)
    if (!result.success) return freshDefaults()
    parsed = result.data
  } catch {
    return freshDefaults()
  }

  // Append any DEFAULT_SIDEBAR entries the stored config didn't have.
  const storedKeys = new Set(parsed.sidebarConfig.map((s) => s.key))
  const merged = [
    ...parsed.sidebarConfig,
    ...DEFAULT_SIDEBAR.filter((d) => !storedKeys.has(d.key)),
  ]
  return {
    ...parsed,
    sidebarConfig: merged.length > 0 ? merged : DEFAULT_SIDEBAR,
  }
}

/** Fresh copy of DEFAULTS (new array / object references) so callers can
 * mutate without affecting other callers' copies. */
export function freshDefaults(): Preferences {
  return {
    ...DEFAULTS,
    sidebarConfig: [...DEFAULT_SIDEBAR],
  }
}

// ─── Resolver ────────────────────────────────────────────────────────────────

/**
 * Resolve the preferences a user should see, given the remote value from
 * Supabase and the local warm-start cache.
 *
 * Behaviour (preserved from pre-Zod implementation):
 *   - If `remote` is a non-null object, use it (server wins; cache is
 *     ignored). Parse through the schema — missing keys take defaults.
 *   - If `remote` is null/undefined, use the local cache. Same parse.
 *
 * This rule prevents the cross-device stale-cache bug: if user deletes
 * a preference on device A, device B's cache still has the old value,
 * but once device B sees server state, it takes server over cache rather
 * than merging the cache on top.
 */
export function resolvePreferences(
  remote: unknown,
  localFallback: unknown,
): Preferences {
  const source = remote != null && typeof remote === 'object' ? remote : localFallback
  return parsePreferences(source)
}
